<template>
  <div>
    <!-- Budget Estimator Modal -->
    <BudgetEstimator 
      :show-modal="showBudgetModal"
      @close="closeBudgetModal"
      @search="handleBudgetSearch"
    />
    
    <!-- Bottom Navigation -->
    <div class="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-300"
         :class="isHidden ? 'translate-y-full' : 'translate-y-0'"
         :style="{ zIndex: navZIndex }">
    <div class="flex justify-around items-center pt-1 pb-1" style="padding-bottom: max(0.25rem, env(safe-area-inset-bottom))">
      <!-- Trip Finder -->
      <button
        @click="navigateToTripFinder"
        class="flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all duration-200 min-w-0 flex-1 group"
        :class="currentOpenModal === 'trip-finder' ? 'text-[#4ECF84]' : 'text-[#2F3033] hover:text-[#4ECF84]'"
      >
        <Icon name="search" size="lg" class="mb-1 transition-transform duration-200 group-active:scale-95" />
        <span class="text-xs font-medium   tracking-tight">Trip Finder</span>
      </button>

      <!-- Budget Estimator -->
      <button
        @click="navigateToBudgetEstimator"
        class="flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all duration-200 min-w-0 flex-1 group"
        :class="currentOpenModal === 'budget' ? 'text-[#4ECF84]' : 'text-[#2F3033] hover:text-[#4ECF84]'"
      >
        <Icon name="calculator" size="lg" class="mb-1 transition-transform duration-200 group-active:scale-95" />
        <span class="text-xs font-medium   tracking-tight">Budget</span>
      </button>

      <!-- Trips -->
      <button
        @click="navigateToSavedTrips"
        class="flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all duration-200 min-w-0 flex-1 relative group"
        :class="isActive('saved') ? 'text-[#4ECF84]' : 'text-[#2F3033] hover:text-[#4ECF84]'"
      >
        <Icon name="map-plus" size="lg" class="mb-1 transition-transform duration-200 group-active:scale-95" />
        <span class="text-xs font-medium   tracking-tight">Trips</span>
        <!-- Optional: Badge for saved trips count -->
        <span
          v-if="savedTripsCount > 0"
          class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
        >
          {{ savedTripsCount > 9 ? '9+' : savedTripsCount }}
        </span>
      </button>

      <!-- TripHub -->
      <router-link
        to="/triphub"
        class="flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all duration-200 min-w-0 flex-1 group no-underline"
        :class="isActive('profile') ? 'text-[#4ECF84]' : 'text-[#2F3033] hover:text-[#4ECF84]'"
      >
        <Target :size="24" :stroke-width="1.5" class="mb-1 transition-transform duration-200 group-active:scale-95" />
        <span class="text-xs font-medium tracking-tight">TripHub</span>
      </router-link>
    </div>

    <!-- Profile Menu Overlay -->
    <div
      v-if="showProfileMenu"
      class="absolute bottom-full left-0 right-0 bg-white border-t border-gray-200 shadow-xl rounded-t-xl"
    >
      <div class="p-4 sm:p-6 space-y-4">
        <!-- Authenticated User -->
        <div v-if="authStore.isAuthenticated">
          <div class="flex items-center space-x-3 mb-3 pb-3 border-b">
            <div class="w-10 h-10 bg-gray-100 rounded-full overflow-hidden flex items-center justify-center">
              <template v-if="profilePhotoUrl">
                <img :src="profilePhotoUrl" alt="User profile photo" class="w-full h-full object-cover" />
              </template>
              <template v-else>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </template>
            </div>
            <div>
              <div class="font-medium text-gray-900">{{ authStore.user?.username }}</div>
              <div class="text-sm text-gray-500">{{ authStore.user?.email }}</div>
            </div>
          </div>
          
          <router-link 
            to="/triphub" 
            @click="closeProfileMenu"
            class="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 active:bg-gray-100  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <span class="font-medium">TripHub</span>
          </router-link>
          
          <router-link
            to="/profile"
            @click="closeProfileMenu"
            class="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 active:bg-gray-100  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span class="font-medium">Settings</span>
          </router-link>

          <router-link
            v-if="authStore.isAdmin"
            to="/admin"
            @click="closeProfileMenu"
            class="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200 active:bg-gray-100 border-t  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span class="font-medium">Admin Dashboard</span>
          </router-link>

          <button
            @click="handleLogout"
            class="flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg w-full text-left transition-colors duration-200 active:bg-red-100  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span class="font-medium">Sign Out</span>
          </button>
        </div>

        <!-- Unauthenticated User -->
        <div v-else class="space-y-3">
          <button 
            @click="handleLogin"
            class="flex items-center justify-center space-x-2 w-full px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg border-2 border-gray-200 transition-colors duration-200 active:bg-gray-100  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            <span>Sign In</span>
          </button>
          
          <button 
            @click="handleRegister"
            class="flex items-center justify-center space-x-2 w-full px-6 py-3 text-base font-medium text-white bg-[#4ECF84] hover:bg-[#4A9B7A] rounded-lg transition-colors duration-200 active:bg-[#3A8B6A]  "
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <span>Sign Up</span>
          </button>
        </div>
      </div>
    </div>
    </div>

    <!-- Backdrop for Profile Menu -->
    <div
      v-if="showProfileMenu"
      class="fixed inset-0 bg-black bg-opacity-25 z-40"
      @click="closeProfileMenu"
    ></div>
    
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import BudgetEstimator from './BudgetEstimator.vue'
import Icon from './Icon.vue'
import { Target } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'
import { apiService } from '@/services/api/index.js'

const emit = defineEmits(['open-trip-finder', 'close-trip-finder', 'open-auth-modal'])
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const showBudgetModal = ref(false)

// State
const showProfileMenu = ref(false)
const savedTripsCount = ref(0)
const searchActive = ref(false)
const isHidden = ref(false)
const lastScrollY = ref(0)
const ticking = ref(false)
const tripFinderModalOpen = ref(false)
const budgetEstimatorModalOpen = ref(false)
let bodyClassObserver = null

// Track which modal/content is currently open
const currentOpenModal = ref(null) // 'trip-finder', 'budget', 'profile', null

const navZIndex = computed(() => (tripFinderModalOpen.value || budgetEstimatorModalOpen.value ? 80 : 50))

// Computed
function isActive(tab) {
  switch (tab) {
    case 'trip-finder':
      return searchActive.value || route.path === '/search-results'
    case 'budget':
      return route.path === '/budget-estimator'
    case 'saved':
      return route.path === '/saved-trips'
    case 'profile':
      return route.path === '/profile' || route.path === '/triphub'
    default:
      return false
  }
}

// Methods
function closeAllModals() {
  // Close all modals/content
  if (currentOpenModal.value === 'trip-finder') {
    emit('close-trip-finder')
  }
  showBudgetModal.value = false
  closeProfileMenu()
  currentOpenModal.value = null
}

function navigateToTripFinder() {
  // If TripFinder is already open, close it
  if (currentOpenModal.value === 'trip-finder') {
    emit('close-trip-finder')
    currentOpenModal.value = null
    searchActive.value = false
    return
  }

  // Close any other open modals first
  closeAllModals()

  // Open TripFinder
  searchActive.value = true
  currentOpenModal.value = 'trip-finder'

  // Always open the modal immediately
  emit('open-trip-finder')

  // Navigate to home if not already there
  if (route.path !== '/') {
    router.push('/')
  }
}

function navigateToBudgetEstimator() {
  // Close any other open modals first
  closeAllModals()
  
  // Open Budget modal
  showBudgetModal.value = true
  currentOpenModal.value = 'budget'
}

function closeBudgetModal() {
  showBudgetModal.value = false
  if (currentOpenModal.value === 'budget') {
    currentOpenModal.value = null
  }
}

function handleBudgetSearch(searchData) {
  console.log('Budget search initiated:', searchData)
  
  // Store the search data in localStorage for the BudgetEstimation page to access
  try {
    localStorage.setItem('budgetEstimatorData', JSON.stringify(searchData))
  } catch (error) {
    console.error('Error storing budget estimator data:', error)
  }
  
  // Close the modal after search
  closeBudgetModal()
  
  // Navigate to the BudgetEstimation page
  router.push('/budget-estimator')
}

function navigateToSavedTrips() {
  // Close any open modals first
  closeAllModals()
  
  searchActive.value = false
  router.push('/saved-trips')
}

function toggleProfileMenu() {
  // If profile menu is already open, close it
  if (showProfileMenu.value) {
    closeProfileMenu()
    return
  }
  
  // Close any other open modals first
  closeAllModals()
  
  // Open profile menu
  showProfileMenu.value = true
  currentOpenModal.value = 'profile'
  // Ensure nav is visible when interacting with the profile menu
  isHidden.value = false
}

function closeProfileMenu() {
  showProfileMenu.value = false
  if (currentOpenModal.value === 'profile') {
    currentOpenModal.value = null
  }
}

const profilePhotoUrl = computed(() => {
  if (!authStore.isAuthenticated) return null

  // Use profile_picture_url (signed URL from backend) first, then fall back to other fields
  const photo = authStore.user?.profile_picture_url || authStore.user?.profile_picture || authStore.user?.avatar_url || authStore.user?.avatar
  if (photo) {
    // If it's already a full URL (signed URL), use as-is
    if (/^https?:\/\//i.test(photo)) {
      return photo
    }
    // Otherwise, construct the uploads URL (proxied to backend in dev)
    return `/uploads/${photo}`
  }

  // Default avatar for authenticated users without a profile photo
  return '/macumba-placeholder.svg'
})

function handleLogin() {
  emit('open-auth-modal', 'login')
  closeAllModals()
}

function handleRegister() {
  emit('open-auth-modal', 'register')
  closeAllModals()
}

async function handleLogout() {
  await authStore.logout()
  closeAllModals()
  router.push('/')
}

// Load saved trips count
async function loadSavedTripsCount() {
  if (!authStore.isAuthenticated) {
    savedTripsCount.value = 0
    return
  }
  
  try {
    const trips = await apiService.getSavedTrips()
    savedTripsCount.value = trips.length
  } catch (error) {
    console.error('Error loading saved trips count:', error)
    savedTripsCount.value = 0
  }
}

// Initialize
onMounted(() => {
  loadSavedTripsCount()
  // Initialize scroll position
  lastScrollY.value = window.scrollY || 0

  const onScroll = () => {
    // If TripFinder modal is open, keep nav visible and ignore hide logic
    if (tripFinderModalOpen.value || budgetEstimatorModalOpen.value) {
      isHidden.value = false
      return
    }
    const currentY = window.scrollY || 0
    const delta = currentY - lastScrollY.value

    // Reveal when at top of page
    if (currentY <= 0) {
      isHidden.value = false
      lastScrollY.value = 0
      return
    }

    // Determine if near bottom; still hide on downward scroll
    const scrollingDown = delta > 2 // small threshold to reduce jitter
    const scrollingUp = delta < -2

    if (scrollingDown) {
      isHidden.value = true
    } else if (scrollingUp) {
      isHidden.value = false
    }

    lastScrollY.value = currentY
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  // Observe body class changes to detect TripFinder modal state
  const updateModalOpen = () => {
    try {
      const wasOpen = tripFinderModalOpen.value
      const wasBudgetOpen = budgetEstimatorModalOpen.value
      tripFinderModalOpen.value = document.body.classList.contains('tripfinder-modal-open')
      budgetEstimatorModalOpen.value = document.body.classList.contains('budgetestimator-modal-open')

      // If TripFinder modal was closed externally, update our state
      if (wasOpen && !tripFinderModalOpen.value && currentOpenModal.value === 'trip-finder') {
        currentOpenModal.value = null
        searchActive.value = false
      }

      if (tripFinderModalOpen.value) {
        // Ensure nav is visible above the modal
        isHidden.value = false
        // Update our state if TripFinder opened externally
        if (currentOpenModal.value !== 'trip-finder') {
          currentOpenModal.value = 'trip-finder'
          searchActive.value = true
        }
      }

      if (budgetEstimatorModalOpen.value) {
        isHidden.value = false
        if (currentOpenModal.value !== 'budget' && showBudgetModal.value) {
          currentOpenModal.value = 'budget'
        }
      }

      if (wasBudgetOpen && !budgetEstimatorModalOpen.value && currentOpenModal.value === 'budget') {
        currentOpenModal.value = null
      }
    } catch (e) {
      // no-op
    }
  }
  updateModalOpen()
  bodyClassObserver = new MutationObserver(updateModalOpen)
  bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })

  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
    if (bodyClassObserver) {
      bodyClassObserver.disconnect()
      bodyClassObserver = null
    }
  })
})
</script>

<style scoped>
/* Add safe area padding for devices with home indicator */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .md\:hidden {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style> 